/**
 * Vincent B871 (Eric 2026-02-14 SPEC STRICK PROD) — Deploy Retry Guide.
 *
 * Badge dans la sidebar admin qui indique si PROD (knoria.com) dérive
 * du preview (dernier commit local). Poll `/api/admin/deploy-status`
 * toutes les 2 min ; visible seulement pour les admins.
 *
 * États visuels :
 *   • Vert emerald  « ✓ PROD à jour »   → in_sync=true
 *   • Ambre         « ⚠ PROD en retard » → in_sync=false (commit preview
 *                                          > moment où hash PROD a été
 *                                          détecté pour la 1re fois)
 *   • Bleu zinc     « ? PROD stable »    → in_sync=null (bootstrap, on
 *                                          n'a pas encore de baseline)
 *   • Rouge         « × Erreur »         → fetch failed
 *
 * Le badge est cliquable : ouvre un popover avec les détails
 * (SHA preview, hash PROD, delta secondes, timestamps ISO).
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle, Warning, Question, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import { getLayoutBootstrap } from "../lib/layoutBootstrap"; // B1099
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 120_000; // 2 minutes

export default function DeployStatusBadge({ compact = false }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [popoverOpen, setPopoverOpen] = useState(false);

  const fetchStatus = useCallback(async ({ fromBootstrap = false } = {}) => {
    try {
      // B1099 (Eric 2026-09-07) — Premier affichage servi par l'appel agrégé
      // du gabarit (mis en cache 20 s, donc les deux barres latérales —
      // bureau et mobile — se partagent la même réponse). Le poll 2 min et
      // le bouton « Recharger » interrogent l'endpoint dédié.
      if (fromBootstrap) {
        const boot = await getLayoutBootstrap();
        if (boot?.deploy_status) {
          setState({ loading: false, data: boot.deploy_status, error: null });
          return;
        }
      }
      const res = await axios.get(`${API}/api/admin/deploy-status`, { withCredentials: true });
      setState({ loading: false, data: res.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || e.message });
    }
  }, []);

  useEffect(() => {
    fetchStatus({ fromBootstrap: true });
    const id = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const { loading, data, error } = state;
  // Tâche 1407 (Eric 2026-10-01) — Badge présent sur CHAQUE page d'admin et
  // jamais traduit : « PROD À JOUR » restait en français sur l'écran anglais.
  const { tr } = useLang();
  const inSync = data?.in_sync;
  const isBootstrap = data?.prod?.bootstrap === true;

  // Palette et libellé selon l'état.
  let palette, label, Icon, title;
  if (loading) {
    palette = "text-zinc-400 bg-zinc-100 border-zinc-200";
    label = "…";
    Icon = ArrowClockwise;
    title = tr("Vérification de l'état PROD…", "Checking PROD state…");
  } else if (error) {
    palette = "text-red-700 bg-red-50 border-red-200";
    label = compact ? "×" : tr("Erreur", "Error");
    Icon = XCircle;
    title = tr(`Erreur : ${error}`, `Error: ${error}`);
  } else if (inSync === true) {
    palette = "text-emerald-700 bg-emerald-50 border-emerald-200";
    label = compact ? "✓" : tr("PROD à jour", "PROD up to date");
    Icon = CheckCircle;
    title = tr(
      `PROD synchronisée. Preview ${data.preview.sha} — commit ${data.preview.ts?.slice(0, 16)}`,
      `PROD in sync. Preview ${data.preview.sha} — commit ${data.preview.ts?.slice(0, 16)}`,
    );
  } else if (inSync === false) {
    palette = "text-amber-800 bg-amber-50 border-amber-300";
    label = compact ? "⚠" : tr(
      `PROD en retard (${data.delta_seconds != null ? Math.floor(data.delta_seconds / 60) + "min" : "?"})`,
      `PROD behind (${data.delta_seconds != null ? Math.floor(data.delta_seconds / 60) + "min" : "?"})`,
    );
    Icon = Warning;
    title = tr(
      "PROD dérive du preview — clique Deploy pour propager les derniers correctifs.",
      "PROD has drifted from preview — click Deploy to ship the latest fixes.",
    );
  } else {
    // bootstrap ou null
    palette = "text-zinc-600 bg-zinc-50 border-zinc-200";
    label = compact ? "?" : (isBootstrap ? tr("PROD (référence)", "PROD (baseline)") : tr("PROD stable", "PROD steady"));
    Icon = Question;
    title = isBootstrap
      ? tr("Premier hash PROD observé — attendez le prochain deploy pour dater précisément.",
           "First PROD hash observed — wait for the next deploy to date it precisely.")
      : tr("État PROD indéterminé.", "PROD state undetermined.");
  }

  const commitMsg = data?.preview?.message || "";
  const shaShort = data?.preview?.sha?.slice(0, 8) || "—";
  const hashShort = data?.prod?.hash || "—";

  return (
    <div className="relative" data-testid="deploy-status-badge-wrapper">
      <button
        type="button"
        onClick={() => setPopoverOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded border text-[10.5px] font-semibold uppercase tracking-wide transition-colors ${palette} hover:brightness-95`}
        data-testid="deploy-status-badge"
        title={title}
        aria-label={label}
      >
        <Icon size={12} weight="bold" />
        <span>{label}</span>
      </button>
      {popoverOpen && data && (
        <div
          className="absolute z-50 mt-1 left-0 min-w-[280px] max-w-[340px] p-3 rounded-lg bg-white border border-zinc-200 shadow-lg text-[11px] leading-relaxed"
          data-testid="deploy-status-popover"
          onMouseLeave={() => setPopoverOpen(false)}
        >
          <div className="font-bold text-zinc-800 mb-2 flex items-center gap-1.5">
            <Icon size={14} weight="bold" className={palette.split(" ")[0]} />
            État déploiement
          </div>
          <div className="space-y-1 text-zinc-700">
            <div>
              <span className="text-zinc-500 uppercase text-[9px]">Preview</span>
              <div className="font-mono text-[10.5px]">
                <span className="text-emerald-700">{shaShort}</span>
                {data.preview.ts && (
                  <span className="text-zinc-500 ml-1.5">— {new Date(data.preview.ts).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                )}
              </div>
              {commitMsg && (
                <div className="text-[10px] text-zinc-500 italic truncate mt-0.5" title={commitMsg}>{commitMsg}</div>
              )}
            </div>
            <div className="border-t border-zinc-100 pt-1.5 mt-1.5">
              <span className="text-zinc-500 uppercase text-[9px]">PROD (knoria.com)</span>
              <div className="font-mono text-[10.5px]">
                <span className="text-amber-800">{hashShort}</span>
                {data.prod.first_seen_at && (
                  <span className="text-zinc-500 ml-1.5">— vu à {new Date(data.prod.first_seen_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })}</span>
                )}
              </div>
            </div>
            {data.delta_seconds != null && (
              <div className="border-t border-zinc-100 pt-1.5 mt-1.5 text-zinc-600">
                Delta commit → PROD : <span className="font-mono font-semibold">{data.delta_seconds > 0 ? "+" : ""}{Math.floor(data.delta_seconds / 60)} min</span>
              </div>
            )}
            {inSync === false && (
              <div className="border-t border-amber-100 pt-1.5 mt-1.5 text-amber-800 font-medium">
                → Redéployez pour propager les correctifs récents.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); fetchStatus(); }}
            className="mt-2 w-full text-center py-1 rounded bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-[10px] font-semibold inline-flex items-center justify-center gap-1"
            data-testid="deploy-status-refresh"
          >
            <ArrowClockwise size={10} weight="bold" /> Recharger
          </button>
        </div>
      )}
    </div>
  );
}
